import { c as _c } from "react/compiler-runtime";
import React from 'react';
import cx from 'classnames';
import { get } from 'lodash';
import { Redirect, useParams } from 'react-router-dom';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import decodeBase64 from 'commons/decodeBase64';
import IconTick from 'components/icons/iconTick';
import CtaLink from 'components/essentials/Cta/CtaLink';
import { GET_ORDER_BUILD_UPDATES } from 'mocks/queries';
import withLoginPage from 'components/consumer/consumer-login/login-page/hoc/withLoginPage';
import CustomerDashboardPageTemplate from 'components/consumer/CustomerDashboardPageTemplate';
import ProductCardLabelUI from 'components/consumer/product-card/ProductCardLabel/ProductCardLabelUI';
import FAQCommonQuestions from 'components/consumer/user-orders-build-updates/FAQCommonQuestions/FAQCommonQuestionsUI';
import useLogoutClient from 'data-hooks/useUserData/useLogoutClient';
const steps = [{
  key: 'initial',
  content: `Before we get started with the build, we make sure to acquire our lumber from ethical and sustainable sources, work with reputable companies that produce beautiful yet durable fabrics, and find the comfiest foam to ensure a cozy seat for years to come.`
}, {
  key: 'preparation',
  content: `We’ll be busy cutting your choice of high-performance fabric, and the high-grade, responsibly-sourced lumber used for framing.`
}, {
  key: 'build',
  content: `Our craftsmen are constructing your frame by hand, while the upholsterers are busy tailoring your piece to make sure every seam is perfect. We’ll quality check your piece to make sure it stands up to your limited-lifetime guarantee standards. All our furniture-building takes place in North America, and we ship using the most eco-friendly ways, using rail and waiting for full containers. It may take a few days more, but the earth certainly thanks us for it.`
}, {
  key: 'ship',
  content: `You’ll be in good hands with our delivery partners, La-Z-Boy, and JB Hunt, with optional contactless and in-home delivery. Our Partners will coordinate your delivery window and prep the furniture in the room of your choice.`
}, {
  key: 'deliver',
  content: `Your custom-made item has found its forever home. The Joybird family is so happy that you have allowed us to build your dream piece and we hope that it is a part of countless good memories.`
}];
export const faqs = [{
  question: 'How accurate is the delivery estimate?',
  answer: <>
        <p>
          Although our estimated delivery dates are pretty accurate, please note
          that this is not a guaranteed delivery date for your order and these
          windows can vary by +/- 10 business days. Delivery to some areas may
          take longer based on the frequency of deliveries to your zip code.
        </p>
        <br />
        <p>
          Our system calculates this estimated delivery date using the total
          amount of time to process your order, including: material allocation,
          time to build your furniture, packaging, transit time to the delivery
          provider, and transit time to the delivery address. You’ll have a more
          accurate idea once your order ships from our distribution facility,
          and once your order is received and scanned at your local shipping
          hub, you will be contacted by the delivery team.
        </p>
      </>
}, {
  question: 'Will the delivery team take away my old sofa?',
  answer: <p>
        It’s recommended that you have your area cleared before your new
        furniture arrives. The delivery team will not remove any old furniture
        and should not be involved in any detailed moving of existing furniture
        due to liability. The delivery teams will not take away any old pieces
        as, in most cases, they have full trucks to make several deliveries
        within a day.
      </p>
}, {
  question: 'What if my furniture does not fit during delivery?',
  answer: <p>
        <span>
          Most of our collections have removable legs that allow for an
          additional 4-6&quot; of clearance, or their bases can be removed with
          a power drill and a #2 square tip drill/screwdriver bit. Please make
          sure you measure the entryway width and height against the item
          purchased to ensure a smooth delivery process. Check out our
          <span>&nbsp;</span>
        </span>
        <CtaLink textClassName="!normal-case" // !tailwind please keep this marker for future reference of removal of all important-prefixes
    to="/fit-guide/" color="brand" target="_blank" hasArrow={false} noLinkPadding size="sm">
          measuring guide
        </CtaLink>
        <span>
          , and contact us with any questions about measuring your space,
          we&apos;re happy to help!
        </span>
      </p>
}, {
  question: 'How do I know when my order has shipped?',
  answer: <p>
        You will receive an email from us as soon as your order ships along with
        a tracking number so you can follow it right to your door. If you signed
        up for SMS (text) alerts, we’ll also send a text message to the mobile
        number you provided us.
      </p>
}, {
  question: 'Is there any way to get my furniture sooner?',
  answer: <p>
        <span>
          Thanks to overwhelming enthusiasm from our customers, we have reached
          our capacity for expedited orders within our factory. We do have a
          number of Ready to Ship items that are available to leave our warehouse in
          as few as 3 business days.&nbsp;
        </span>
        <span>
          <CtaLink textClassName="!normal-case" to="/instock/" color="brand" target="_blank" hasArrow={false} noLinkPadding size="sm">
            Browse all our in-stock products.
          </CtaLink>
        </span>
      </p>
}];
const BuildUpdatesPage = () => {
  const $ = _c(14);
  const {
    orderid
  } = useParams();
  let t0;
  if ($[0] !== orderid) {
    t0 = decodeBase64(orderid);
    $[0] = orderid;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const order_id = t0;
  const logoutClient = useLogoutClient();
  let t1;
  if ($[2] !== order_id) {
    t1 = {
      variables: {
        order_id
      }
    };
    $[2] = order_id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: buildUpdatesData,
    loading: buildUpdatesLoading
  } = useSuspenseQuery(GET_ORDER_BUILD_UPDATES, t1);
  let t2;
  if ($[4] !== buildUpdatesData?.buildUpdates || $[5] !== buildUpdatesLoading || $[6] !== logoutClient) {
    t2 = () => {
      if (buildUpdatesLoading) {
        return <div className="loading-data">Loading...</div>;
      }
      const {
        build_updates: buildUpdates,
        errors
      } = buildUpdatesData?.buildUpdates || {};
      if (errors) {
        const code = get(errors, "0.code");
        const message = get(errors, "0.message");
        if (code === "AUTH_REQUIRED" || message?.includes("JWT")) {
          logoutClient();
          return <Redirect to="/sign-in/" />;
        }
      }
      if (!buildUpdates) {
        return <div><h1>Unable to load build details</h1></div>;
      }
      return <>{buildUpdates?.ets_date && <div className="text-3xl font-bold [&_span]:max-[767px]:block"><span>Estimated Shipping Date: </span><span>{buildUpdates?.ets_date}</span></div>}<div className="flex flex-col-reverse md:flex-row md:justify-between"><div className="w-[55%] max-[767px]:w-full"><div className="build-updates__steps">{steps.map(step => {
                const {
                  key
                } = step;
                const {
                  date,
                  label,
                  completed
                } = buildUpdates?.[key] || {};
                const isDone = completed === "1";
                const isInProgress = completed === "In Progress";
                const isPending = completed === "0" || !isDone && !isInProgress;
                return <div key={key} className={cx("flex p-6 [border-bottom:1px_solid_#f6f6f6] md:mx-0 md:my-8 md:p-8 md:rounded-lg md:border md:border-solid md:border-gray-light8", {
                  "text-white bg-brand-dark [background-image:url(https://joybird2.imgix.net/user-uploads-staging/joybird-teal-new.jpg?w=400&cs=srgb)] bg-cover": isInProgress || isDone,
                  "build-updates__step--pending": isPending
                })}><div className="pt-[.2rem] pr-6">{isDone && <div className="w-5 h-5 flex items-center justify-center rounded-[50%] bg-brand-dark [&_svg]:h-4"><IconTick /></div>}{isPending && <div className="w-5 h-5 rounded-[50%] border border-solid border-gray-light6" />}{isInProgress && <div className="w-10 h-10 flex items-center justify-center overflow-hidden relative"><div className="rounded-[50%] bg-brand-lightest w-5 h-5 absolute opacity-0 animate-pulse-fade-and-scale [animation-delay:0]" /><div className="rounded-[50%] bg-brand-lightest w-5 h-5 absolute opacity-0 animate-pulse-fade-and-scale [animation-delay:1s]" /><div className="rounded-[50%] bg-brand-lightest w-5 h-5 absolute opacity-0 animate-pulse-fade-and-scale [animation-delay:2s]" /><div className="rounded-[50%] bg-brand-lightest w-5 h-5 absolute opacity-0 animate-pulse-fade-and-scale [animation-delay:3s]" /></div>}</div><div className="build-updates__step-body"><div className={cx("text-2xl font-bold mb-[.325rem] lowercase first-letter:uppercase", {
                      "text-white": isInProgress || isDone,
                      "build-updates__step-title--pending": isPending
                    })}>{label}</div><div className={cx("text-sm mb-[.325rem]", {
                      "text-white": isInProgress || isDone,
                      "build-updates__step-content--pending": isPending
                    })}>{step.content}</div>{isDone && date && <div className="h-8 relative"><ProductCardLabelUI label={`Completed on ${date}`} variant="build-updates-done" /></div>}</div></div>;
              })}</div></div><div className="w-[42%] max-[767px]:w-full max-[767px]:px-4 max-[767px]:py-0"><FAQCommonQuestions steps={faqs} /></div></div></>;
    };
    $[4] = buildUpdatesData?.buildUpdates;
    $[5] = buildUpdatesLoading;
    $[6] = logoutClient;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const renderPage = t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      label: "Orders",
      link: "/customer/orders"
    };
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = `Build Updates: Order J-(${order_id})`;
  let t5;
  if ($[9] !== renderPage) {
    t5 = <div className="w-full">{renderPage()}</div>;
    $[9] = renderPage;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className="build-updates__page" data-xc="build-updates-page"><CustomerDashboardPageTemplate parentPageItem={t3} showTitle={false} title={t4} variant="BuildUpdatesPage">{t5}</CustomerDashboardPageTemplate></div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
export default withLoginPage(BuildUpdatesPage);